import { Injectable } from '@angular/core';

/**
 * Service responsible for capturing screenshots of the user's screen or browser window
 * using the native browser MediaDevices API.
 */
@Injectable({ providedIn: 'root' })
export class ScreenshotService {
    /**
     * Triggers the browser's screen capture prompt to let the user select a target,
     * then extracts a single frame and downloads it as a PNG image.
     * This feature requires a Secure Context (HTTPS or localhost).
     *
     * @returns A promise that resolves when the capture flow is completed.
     * @throws Error if the canvas context cannot be initialized or if the user cancels the prompt.
     */
    public async captureAndDownload(): Promise<void> {
        // 1. Safety Check for Secure Context
        if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
            console.error(
                'Screenshot API is not available (it requires a Secure Context / HTTPS or localhost)',
            );
            alert('Screenshot feature requires a secure connection (HTTPS or localhost).');
            return;
        }

        let stream: MediaStream | null = null;
        try {
            stream = await navigator.mediaDevices.getDisplayMedia({
                video: true,
                audio: false,
            });

            // Create a hidden video element to grab the frame
            const video = document.createElement('video');
            video.srcObject = stream;

            // Wait for video to be ready
            await new Promise((resolve) => {
                video.onloadedmetadata = () => {
                    video.play().then(resolve);
                };
            });

            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            const ctx = canvas.getContext('2d');
            if (!ctx) throw new Error('Could not get canvas context');

            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

            const dataUrl = canvas.toDataURL('image/png');
            this.download(dataUrl);
        } catch (err: any) {
            console.error('Screenshot failed:', err);
            throw err;
        } finally {
            if (stream) {
                stream.getVideoTracks().forEach((t) => t.stop());
            }
        }
    }

    /**
     * Programmatically creates a download link and triggers it to save the screenshot.
     *
     * @param dataUrl - The base64 encoded PNG data of the screenshot.
     */
    private download(dataUrl: string): void {
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = `screenshot-${Date.now()}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }
}
